import { ISourceDataNote, ISourceDataSubnote, NoteType } from '@/api/dtos';
import { ISourceDataFormFieldComponentProps, ISourceDataVerificationItem } from '@/utilities/forms/SourceDataFormProcessor';
import { faClipboardQuestion, faCalendarDays, faUser } from '@fortawesome/duotone-light-svg-icons';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppBar, Box, Dialog, Divider, IconButton, Paper, Slide, styled, Tab, Tabs, Toolbar, Typography, lighten, darken, Chip, Tooltip, alpha } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { FC, forwardRef, ReactElement, Ref, useMemo, useState, useEffect } from 'react';
import ConfigurationFormEditInfo, { ConfigurationTitle } from '../../configuration/edit/ConfigurationFormEditInfo';
import SourceDataInfo, { ISourceDataInfoProps, SourceDataBox, SourceDataTitle } from './SourceDataInfo';
import { NoteStatus } from '../../../../api/dtos';
import { DateTime } from 'luxon'
import SourceDataNote from './SourceDataNote';

const InfoBox = styled(Paper)(({theme}) => ({
    padding: theme.spacing(0, 2),
    margin: theme.spacing(0, 2, 2, 2)
}));

const SourceDataNotesBox = styled(Paper)(({theme}) => ({
    padding: theme.spacing(2, 2, 0, 2),
    margin: theme.spacing(0, 2, 2, 2)
}));

const Transition = forwardRef(function Transition(
    props: TransitionProps & {
      children: ReactElement;
    },
    ref: Ref<unknown>,
  ) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface ISourceDataNotesProps {
    item?: ISourceDataVerificationItem
    open?: boolean;
    onClose?: () => void;
    notes?: ISourceDataNote[];
}

const SourceDataNotes: FC<ISourceDataNotesProps> = ({
    notes,
    item,
    open,
    onClose
}) => {

    const [tab, setTab] = useState(0);

    const handleChange = (event: any, newValue: any) => {
        setTab(newValue);
    };

    // 0 - All
    // 1 - Query
    // 2 - Validation Failure
    // 3 - Internal
    // 4 - Protocol Deviation
    // 5 - Important Protocol Deviation (GABE FIX THIS)
    // 6 - Annotation
    // 7 - Reason For Change

    const tabs = useMemo(() => {
        let count = 0;
        const t = {
            query: false,
            validationFailed: false,
            internal: false,
            protocolDeviation: false,
            importantProtocolDeviation: false,
            annotation: false,
            reasonForChange: false,
            all: false
        }

        if (notes?.some(x => x.type === NoteType.Query)) {
            t.query = true;
            count++;
        }

        if (notes?.some(x => x.type === NoteType.ValidationFailed)) {
            t.validationFailed = true;
            count++;
        }

        if (notes?.some(x => x.type === NoteType.Internal)) {
            t.internal = true;
            count++;
        }

        if (notes?.some(x => x.type === NoteType.ImportantProtocolDeviation)) {
            t.importantProtocolDeviation = true;
            count++;
        }

        if (notes?.some(x => x.type === NoteType.ProtocolDeviation)) {
            t.protocolDeviation = true;
            count++;
        }

        if (notes?.some(x => x.type === NoteType.Annotation)) {
            t.annotation = true;
            count++;
        }

        if (notes?.some(x => x.type === NoteType.ReasonForChange)) {
            t.reasonForChange = true;
            count++;
        }

        if (count > 1) {
            t.all = true;
        }

        return t;
    }, [notes]);

    useEffect(() => {
        if (tabs.all) {
            setTab(0);
        }
        else if (tabs.query) {
            setTab(1);
        }
        else if (tabs.validationFailed) {
            setTab(2);
        }
        else if (tabs.internal) {
            setTab(3);
        }
        else if (tabs.importantProtocolDeviation) {
            setTab(4);
        }
        else if (tabs.protocolDeviation) {
            setTab(5);
        }
        else if (tabs.annotation) {
            setTab(6);
        }
        else if (tabs.reasonForChange) {
            setTab(7);
        }
    }, [tabs])

    const filteredNotes = useMemo(() => {
        switch (tab) {
            case 0:
                return notes ?? [];
            case 1:
                return notes?.filter(x => x.type === NoteType.Query) ?? [];
            case 2:
                return notes?.filter(x => x.type === NoteType.ValidationFailed) ?? [];
            case 3:
                return notes?.filter(x => x.type === NoteType.Internal) ?? [];
            case 4:
                return notes?.filter(x => x.type === NoteType.ImportantProtocolDeviation) ?? [];
            case 5:
                return notes?.filter(x => x.type === NoteType.ProtocolDeviation) ?? [];
            case 6:
                return notes?.filter(x => x.type === NoteType.Annotation) ?? [];
            case 7:
                return notes?.filter(x => x.type === NoteType.ReasonForChange) ?? [];
        }

        return [];
    }, [notes, tab]);

    return (
        <>
            <Dialog
                fullScreen
                open={open ?? false}
                onClose={onClose}
                TransitionComponent={Transition}
            >
                <AppBar position='sticky'>
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={onClose}
                            aria-label="close"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            View Discrepancy Notes
                        </Typography>
                    </Toolbar>
                </AppBar>
                {
                    open && (
                        <>
                            <SourceDataTitle variant="h2">
                                Details
                            </SourceDataTitle>
                            <InfoBox>
                                <SourceDataInfo
                                    {...item}
                                    dividers
                                />
                            </InfoBox>
                            <SourceDataTitle variant="h2">
                                Notes
                            </SourceDataTitle>
                            <SourceDataNotesBox>
                                <Box sx={{ mt: -2, mx: -2, mb: 2}}>
                                    <Tabs value={tab} onChange={handleChange}>
                                        <Tab
                                            sx={{display: tabs.all ? undefined : 'none'}}
                                            label="All"
                                        />
                                        <Tab 
                                            sx={{display: tabs.query ? undefined : 'none'}}
                                            label="Query" 
                                        />
                                        <Tab 
                                            sx={{display: tabs.validationFailed ? undefined : 'none'}}
                                            label="Validation Failure" 
                                        />
                                        <Tab 
                                            sx={{display: tabs.internal ? undefined : 'none'}}
                                            label="Internal"
                                        />
                                        <Tab
                                            sx={{ display: tabs.importantProtocolDeviation ? undefined : 'none' }}
                                            label="Important Protocol Deviation"
                                        />
                                        <Tab 
                                            sx={{display: tabs.protocolDeviation ? undefined : 'none'}}
                                            label="Protocol Deviation" 
                                        />
                                        <Tab 
                                            sx={{display: tabs.annotation ? undefined : 'none'}}
                                            label="Annotation" 
                                        />
                                        <Tab 
                                            sx={{display: tabs.reasonForChange ? undefined : 'none'}}
                                            label="Reason For Change"
                                        />
                                    </Tabs>
                                    <Divider />
                                </Box>
                                {
                                    filteredNotes.map((x, i) => {
                                        return (
                                            <SourceDataNote
                                                key={`${tab}-${i}`}
                                                {...x}
                                            />
                                        )
                                    })
                                }
                            </SourceDataNotesBox>
                        </>
                    )
                }
            </Dialog>
        </>
    );
}

export default SourceDataNotes;